import './styles.scss';

import { ReactElement, useMemo } from 'react';
import ReportTableHeader, { ReportTableHeaderProps } from '@/pages/Reports/DynamicReport/components/ReportTable/ReportTableHeader';
import Table, { TableProps } from '@/common/Table';

import { IS_NUMERIC_STRING } from '@/constants';
import ReportTableRow from '@/pages/Reports/DynamicReport/components/ReportTable/ReportTableRow';
import { ReportTypeEnum } from '@/models/gen/graphql';
import { parseStatusValue } from '@/pages/Reports/DynamicReport/components/ReportTable/components/StatusCell';
import { splitNestedColumns } from '@/pages/Reports/DynamicReport/components/ReportTable/utils';
import { createComponentState } from '@/state';
import { uuid } from '@/utils';

export type ReportTableProps = TableProps & {
  columns?: ReportTableHeaderProps['columns'];
  rows?: unknown[];
  onDetails?: (detailsObj: Record<string, unknown>) => void;
  reportType?: string;
};

export type ReportTableState = {
  sorting: {
    sortBy: string | undefined;
    direction: 'asc' | 'desc' | undefined;
  };
  onSort: (key: string) => () => void;
  expanded: string[];
  onExpand: (id: string) => void;
  getIds: (currentRow: any) => string[];
};
export const useReportTable = createComponentState<ReportTableState>((setState, getState) => {
  const getIds = (currentRow: any): string[] => {
    const ids = [currentRow?.['id']];
    currentRow?.['children']?.forEach((child: any) => {
      if (child?.['id']) ids.push(child?.['id']);
      if (child?.['children']?.length > 0) ids.push(...getIds(child));
    });
    return Array.from(new Set(ids.filter(Boolean)));
  };
  return {
    sorting: {
      sortBy: undefined,
      direction: undefined,
    },
    onSort: (key: string) => () => {
      setState((current) => {
        const direction =
          current.sorting.sortBy === key
            ? current.sorting.direction === undefined
              ? 'asc'
              : current.sorting.direction === 'asc'
                ? 'desc'
                : undefined
            : 'asc';
        return { ...current, sorting: { sortBy: key, direction } };
      });
    },
    expanded: [],
    onExpand: (id: string): void => {
      const {
        state: { expanded },
      } = getState();
      if (expanded.includes(id)) {
        setState((current) => ({ ...current, expanded: Array.from(new Set([...current.expanded].filter((i: string) => i !== id))) }));
      } else {
        setState((current) => ({ ...current, expanded: Array.from(new Set([...expanded, id])) }));
      }
    },
    getIds,
  };
});

const ReportTable = ({ columns, rows, onDetails, reportType, ...tableProps }: ReportTableProps): ReactElement => {
  const [{ sorting }] = useReportTable(({ state }) => {
    return [
      {
        sorting: state.sorting,
      },
    ];
  });

  const parsedRows = useMemo(() => {
    // trainingReport sends its nested column layout as the first child row (see splitNestedColumns).
    const inferNestedColumns = reportType === 'trainingReport';

    const addDetails = (row, parent?, rowColumns?) => {
      if (!Array.isArray(row?.details)) row.details = [row.details];
      const rowDetails = [...(row?.details || [])];
      const tableFilters = {};
      const rowFilters = { ...tableFilters };
      if (parent) {
        row?.details?.forEach?.(({ filters = [], ...rest }: { filters?: Record<string, unknown>[] }): void => {
          filters.forEach((filterObj: Record<string, unknown> = {}): void => {
            Object.entries(filterObj).forEach(([key, value]: any): void => {
              if (value !== null) rowFilters[key] = value;
            });
          });
          Object.entries(rest).forEach(([key, value]: any): void => {
            rowDetails.push({ [key]: value });
          });
        });
      }
      row?.details?.forEach?.(({ filters = [], ...rest }: { filters?: Record<string, unknown>[] }): void => {
        filters.forEach((filterObj: Record<string, unknown> = {}): void => {
          Object.entries(filterObj).forEach(([key, value]: any): void => {
            if (value !== null) rowFilters[key] = value;
          });
        });
        Object.entries(rest).forEach(([key, value]: any): void => {
          rowDetails.push({ [key]: value });
        });
      });
      const id = row?.['id'] || uuid();
      // trainingReport driver rows carry a hidden `attemptId`; surface it in the details payload
      // (only for a completed status) so DynamicReport can open the read-only Evaluation Summary
      // for that row instead of the generic detailed-report drawer.
      const rowStatus = Object.values(row?.values || {})
        .map((value: unknown) => (typeof value === 'string' ? parseStatusValue(value)?.status : undefined))
        .find(Boolean);
      const evaluationFilters =
        inferNestedColumns &&
        typeof row?.values?.attemptId === 'string' &&
        (rowStatus === 'PASS' || rowStatus === 'FAIL' || rowStatus === 'MORE_TRAINING_REQUIRED')
          ? { attemptId: row.values.attemptId }
          : {};
      const filters = { reportType: ReportTypeEnum.DetailedReport, ...rowFilters, ...evaluationFilters };
      const { childColumns, childRows } = inferNestedColumns
        ? splitNestedColumns(row?.['children'] || [], rowColumns)
        : { childColumns: rowColumns, childRows: row?.['children'] || [] };
      const children = childRows.map((child: any) => addDetails(child, row, childColumns));
      const details = rowDetails;
      return { ...row, id, children, details, filters, columns: rowColumns, childColumns };
    };
    return rows.map((row: any) => addDetails(row));
  }, [rows, reportType]);

  const sortedRows = useMemo(() => {
    if (!sorting.sortBy || !sorting.direction) return parsedRows;
    const sort = (rowsToSort: any[]): any[] => {
      return [...rowsToSort]
        .sort((a, b) => {
          let aValue = a?.values?.[sorting.sortBy] ?? '';
          let bValue = b?.values?.[sorting.sortBy] ?? '';
          if (aValue.match(IS_NUMERIC_STRING) && bValue.match(IS_NUMERIC_STRING)) {
            aValue = parseFloat(aValue.replace(/[^0-9.-]+/g, ''));
            bValue = parseFloat(bValue.replace(/[^0-9.-]+/g, ''));
            return (aValue - bValue) * (sorting.direction === 'asc' ? 1 : -1);
          }
          return aValue.localeCompare(bValue) * (sorting.direction === 'asc' ? 1 : -1);
        })
        .map((row) => {
          if (!row?.children?.length) return row;
          return { ...row, children: sort(row.children || []) };
        });
    };
    return sort(parsedRows);
  }, [parsedRows, sorting]);

  return (
    <Table {...tableProps} className="ReportTable" header={<ReportTableHeader columns={columns} rows={sortedRows} />}>
      {sortedRows?.map(
        (row: any, index: number): ReactElement => (
          <ReportTableRow columns={columns} index={index} row={row} onDetails={onDetails} key={index} />
        )
      )}
    </Table>
  );
};

export default ReportTable;
